import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { Logo } from "@/components/brand/Logo";
import { useAuth } from "@/context/AuthContext";

export default function VerifyEmail() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { verifyEmail, verifyRecovery, checkAuth, user } = useAuth();
  const [state, setState] = useState("verifying"); // verifying | ok | error
  const [msg, setMsg] = useState("");
  const [recovery, setRecovery] = useState(false);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    const token = params.get("token");
    const recToken = params.get("recovery");
    if (!token && !recToken) { setState("error"); setMsg("This verification link is missing its token."); return; }
    setRecovery(!!recToken);
    (async () => {
      try {
        if (recToken) await verifyRecovery(recToken);
        else await verifyEmail(token);
        await checkAuth();
        setState("ok");
      } catch (e) {
        setState("error");
        setMsg(e.message);
      }
    })();
  }, [params, verifyEmail, verifyRecovery, checkAuth]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-am-bg px-4">
      <div className="am-radial-bg pointer-events-none absolute inset-0 opacity-40" />
      <div className="relative w-full max-w-md rounded-3xl border border-am-border bg-am-surface/70 p-8 text-center backdrop-blur-xl">
        <Logo size={60} className="mx-auto" />
        {state === "verifying" && (
          <div className="mt-6" data-testid="verify-loading">
            <Loader2 className="mx-auto h-8 w-8 animate-spin aura-text" />
            <p className="mt-4 text-sm text-am-text2">Confirming your email…</p>
          </div>
        )}
        {state === "ok" && (
          <div className="mt-6" data-testid="verify-ok">
            <CheckCircle2 className="mx-auto h-10 w-10 text-aura-mint" />
            <h1 className="mt-4 font-display text-2xl font-extrabold text-white">{recovery ? "New email confirmed." : "Email confirmed."}</h1>
            <p className="mt-2 text-sm text-am-text2">{recovery ? "Your email has been updated and verified. You're ready to post and max your aura." : "Your account is verified. You're ready to post and max your aura."}</p>
            <button onClick={() => navigate(user ? "/home" : "/login")} data-testid="verify-continue" className="mt-6 w-full rounded-full aura-gradient py-3 text-sm font-bold text-black">
              {user ? "Enter AuraMax" : "Log in"}
            </button>
          </div>
        )}
        {state === "error" && (
          <div className="mt-6" data-testid="verify-error">
            <XCircle className="mx-auto h-10 w-10 text-aura-magenta" />
            <h1 className="mt-4 font-display text-2xl font-extrabold text-white">Link not valid</h1>
            <p className="mt-2 text-sm text-am-text2">{msg || "This link is invalid or has expired."}</p>
            <button onClick={() => navigate(user ? "/home" : "/login")} data-testid="verify-continue" className="mt-6 w-full rounded-full border border-am-border-strong py-3 text-sm font-semibold text-white hover:bg-white/5">
              {user ? "Back to AuraMax" : "Back to log in"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
